import { ImageViewerModal, ImageViewerModalRef } from "@/components/image-viewer-modal";
import { FocusWordsManagerForm, FocusWordsManagerFormRef } from "@/components/focus-words-manager-form";

import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { FocusWordsLocale } from "@/locales/localeid";
import { FocusWordModel } from "@/models/focus-words-manager-model";
import { ImageMetaData } from "@/models/image-meta-data";
import { VCSLevelModel } from "@/models/title-list-model";
import TitleListState from "@/modules/title-list";
import { VersionGroupModule } from "@/modules/versiongroup";
import { FocusWordsManagerService } from "@/services/focus-words-manager-service";
import { AllLevel } from "@/util/constants";
import { disabledDueToAccessLevel, getRouteAccessLevel } from "@/util/func";
import { Alert, Button, Checkbox, Col, Input, Radio, Row, Select } from "ant-design-vue";
import { ActionItem, fmtMsg, isGuid } from "commonui";
import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { CurriculumType } from "@/util/enum";

@Component({
	components: {
		Grid,
		Row,
		Col,
		Input,
		RadioGroup: Radio.Group,
		Radio,
		Select,
		Option: Select.Option,
		Checkbox,
		Button,
		ActionItem,
		Alert,
		ImageViewerModal,
		FocusWordsManagerForm
	}
})
export default class FocusWordsManager extends Vue {
	public readonly locale = FocusWordsLocale;
	public versionGroupModule = VersionGroupModule;
	public titleListModule = getModule(TitleListState);
	public searchValue: string = "";
	public unitSelected: number = AllLevel;
	public readonly allLevel = AllLevel;
	public loading: boolean = false;
	public filter = this.getDefaultFilterValues();
	public filteredData: FocusWordModel[] = [];
	public imageViewerSrc: string = "";
	public sasUrl: any = "";
	public initialUnitId: number = AllLevel;
	public gsId: string = "";
	public lsId: string = "";
	imageViewerMetaData?: ImageMetaData = {
		x: 0,
		y: 0,
		width: 0,
		height: 0,
		rotate: 0,
		scaleX: 0,
		scaleY: 0,
		annotations: []
	};
	public selectedWordId: number = -1;
	public isDrawerVisible: boolean = false;
	public clickedWordStartUnitId: number = 0;

	$refs!: {
		liteViewer: ImageViewerModalRef;
	};

	created() {
		this.onUpdateVersion();
	}

	mounted() {
		VersionGroupModule.setDisabled(false);
		this.getImageViewingSas();
	}

	destroyed() {
		VersionGroupModule.setDisabled(true);
	}

	getImageViewingSas() {
		FocusWordsManagerService.getImageViewingSas().then((res) => {
			this.sasUrl = res;
		});
	}

	public get isAnyVersionSelected(): boolean {
		return this.isGSVersionSelected || this.isLSVersionSelected;
	}
	public get isGSVersionSelected(): boolean {
		this.gsId = this.versionGroupModule.selectedGsVersion;
		return !!isGuid(this.versionGroupModule.selectedGsVersion);
	}
	public get isLSVersionSelected(): boolean {
		this.lsId = this.versionGroupModule.selectedLsVersion;
		return !!isGuid(this.versionGroupModule.selectedLsVersion);
	}

	public get levels(): VCSLevelModel[] {
		/*
		 If selected GrapeSeed version is None the select only LittleSeed units.
		 If selected LittleSeed version is None the select only GrapeSeed units.
		 If both GrapeSeed and LittleSeed are None then return empty array.
		 If both GrapeSeed and LittleSeed are selected then return all the units.
		*/
		if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 0);
		} else if (VersionGroupModule.selectedLsVersion == "-1" && VersionGroupModule.selectedGsVersion != "-1") {
			return this.titleListModule.levels.filter((l) => l.curriculumType != 1);
		} else if (VersionGroupModule.selectedGsVersion == "-1" && VersionGroupModule.selectedLsVersion == "-1") {
			return [];
		} else {
			return this.titleListModule.levels;
		}
	}

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	public close() {
		this.isDrawerVisible = false;
	}

	onAddClick() {
		this.setFocusWordId(0);
		this.isDrawerVisible = true;
	}

	fetchUpdatedList() {
		this.fetch();
	}

	onDisplayClick() {
		this.fetch();
	}

	setFocusWordId(value) {
		this.selectedWordId = value;
	}

	//open edit drawer
	handleTitleClick(value) {
		this.clickedWordStartUnitId = value.startUnitId;
		this.setFocusWordId(value.id);
		this.isDrawerVisible = true;
	}

	@Watch("versionGroupModule.selectedLsVersion")
	@Watch("versionGroupModule.selectedGsVersion")
	onUpdateVersion() {
		if (this.isAnyVersionSelected) {
			this.filter = this.getDefaultFilterValues();
			this.initialUnitId = this.getDefaultFilterValues().level;
			this.unitSelected = this.getDefaultFilterValues().level;
			this.fetch();
		} else {
			this.filter = [];
		}
	}

	onUnitChange(value) {
		this.unitSelected = value;
	}

	private getDefaultFilterValues(): any {
		let defaultUnitId: VCSLevelModel | undefined;

		if (this.isGSVersionSelected) {
			defaultUnitId = this.titleListModule.levels.find(
				(level) => level.curriculumType === CurriculumType.GrapeSEED
			);
		} else if (this.isLSVersionSelected) {
			defaultUnitId = this.titleListModule.levels.find(
				(level) => level.curriculumType === CurriculumType.LittleSEED
			);
		}

		return {
			level: defaultUnitId?.id || AllLevel
		};
	}

	private getAdditionalUnitsString(additionalUnits) {
		let res = "";
		additionalUnits.forEach((ele) => {
			res = res + ele.displayName + ", ";
		});
		res = res.substring(0, res.length - 2);
		return res;
	}

	createViewingUrl(imageName: string) {
		const urlArray = this.sasUrl.url.split("/");
		return urlArray[0] + "//" + urlArray[2] + "/" + this.sasUrl.name + "/" + imageName + urlArray[3];
	}

	onLiteViewerClick(value) {
		this.imageViewerSrc = this.createViewingUrl(value.url) || "";
		this.$refs.liteViewer.open();
	}

	onLiteViewerClose() {
		this.imageViewerSrc = "";
		this.imageViewerMetaData = undefined;
	}

	private updateFilteredData() {
		this.filteredData.forEach((currentWord: FocusWordModel, index) => {
			if (currentWord.havingAudio == false) {
				this.filteredData[index].havingAudio = fmtMsg(this.locale.GridNoText);
			} else if (currentWord.havingAudio == true) {
				this.filteredData[index].havingAudio = fmtMsg(this.locale.GridYesText);
			}
			if (currentWord.havingDefinitionAudio == false) {
				this.filteredData[index].havingDefinitionAudio = fmtMsg(this.locale.GridNoText);
			} else if (currentWord.havingDefinitionAudio == true) {
				this.filteredData[index].havingDefinitionAudio = fmtMsg(this.locale.GridYesText);
			}

			if (currentWord.additionalUnits.length) {
				this.filteredData[index].additionalUnits = this.getAdditionalUnitsString(currentWord.additionalUnits);
			}
			this.filteredData[index].havingImage = currentWord.image
				? fmtMsg(this.locale.GridYesText)
				: fmtMsg(this.locale.GridNoText);
		});
	}

	private fetch() {
		this.loading = true;
		FocusWordsManagerService.getFocusWords({
			gsVersionGroupId:
				VersionGroupModule.selectedGsVersion !== "-1" ? VersionGroupModule.selectedGsVersion : null,
			lsVersionGroupId:
				VersionGroupModule.selectedLsVersion !== "-1" ? VersionGroupModule.selectedLsVersion : null,
			unitId: this.unitSelected,
			searchText: this.searchValue
		})
			.then((focusWords) => {
				this.filteredData = focusWords;
				this.updateFilteredData();
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	get initialUnit() {
		return this.titleListModule.levels.find((l) => l.id === this.initialUnitId)?.unit;
	}

	get startUnitId() {
		return this.clickedWordStartUnitId;
	}
}
